import React, { FC, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Link, useParams } from 'react-router'

import { Bar } from '@ant-design/plots'
import { Col, Flex, Image, Row, Tabs } from 'antd'

import { HTML } from '../../components/markup/HTML'
import { Page } from '../../components/page'
import { Title } from '../../components/text/Title'
import { useTrackQuery } from '../../graphql'
import NotFound from '../../pages/NotFound'
import { Context as ConfigContext } from '../../providers/Config'
import routes from '../../routes'

import { Information } from './Information'
import { Records } from './Records'

const Track: FC = () => {
  const { slug = '' } = useParams<{ slug: string }>()
  const { theme } = useContext(ConfigContext)
  const { formatMessage } = useIntl()
  const { data, loading } = useTrackQuery({ variables: { slug } })
  const track = data?.track
  const records: MotoGP.TrackRecords = track?.records

  const bestRecords = useMemo(() => {
    const riders: Record<string, { name: string; slug: string, type: string } & { count: number }> = {}
    const bikes: Record<string, { name: string; slug: string } & { count: number }> = {}
    track?.results.forEach((it) => {
      if (it?.rider?.name) {
        const count = Number(riders[`${it?.rider?.name}`]?.count) || 0
        riders[it?.rider?.name] = { count: count + 1, type: it.type, ...it.rider }
      }
      if (it?.bike?.name) {
        const count = Number(bikes[`${it?.bike?.name}`]?.count) || 0
        bikes[it?.bike?.name] = { count: count + 1, ...it.bike }
      }
    })

    return { riders: Object.values(riders), bikes: Object.values(bikes) }
  }, [track?.results])

  if (!track && !loading) return <NotFound />

  return (
    <Page
      loading={loading}
      title={data?.track?.name}
      breadcrumbs={[
        {
          title: <Link to={routes.tracks}>{formatMessage({ id: 'header.navigation.tracks' })}</Link>,
        },
      ]}
      seo={{
        ...track?.seo,
        metaTitle: data?.track?.name ? [data?.track?.name, formatMessage({ id: 'tracks.seo.title' })].join(' | ') : '',
      }}
    >
      <Row gutter={[24, 24]} align={'top'} justify={'start'}>
        <Col xs={24} lg={8}>
          <Flex vertical gap={12} align={'center'} justify={'center'}>
            <Image style={{ margin: '0 auto' }} height={360} width={'100%'} preview={false} src={track?.layout.url} />
          </Flex>
        </Col>
        <Col xs={24} lg={6}>
          <Information {...track} />
        </Col>
        {records ? (
          <Col xs={24} lg={10}>
            <Flex vertical gap={0}>
              <Title level={4}>{formatMessage({ id: 'track.records' })}</Title>
              <Tabs
                items={Object.entries(records)
                  .slice()
                  .reverse()
                  .map(([racingClass, record]) => ({
                    label: racingClass,
                    key: racingClass,
                    children: <Records record={record} />,
                  }))}
              />
            </Flex>
          </Col>
        ) : null}

        <Col xs={24} lg={24}>
          <Title level={3}>{formatMessage({ id: 'track.wins' })}</Title>
          <Tabs
            items={[
              {
                label: formatMessage({ id: 'track.wins.riders' }),
                key: 'riders',
                children: (
                  <Bar
                    theme={theme}
                    data={bestRecords.riders}
                    height={24 * bestRecords.riders.length + 48}
                    stack
                    sort={{
                      reverse: true,
                      by: 'y',
                    }}
                    legend={false}
                    xField={'name'}
                    yField={'count'}
                    colorField={'name'}
                  />
                ),
              },
              {
                label: formatMessage({ id: 'track.wins.bikes' }),
                key: 'bikes',
                children: (
                  <Bar
                    theme={theme}
                    data={bestRecords.bikes}
                    height={24 * bestRecords.bikes.length + 48}
                    sort={{
                      reverse: true,
                      by: 'y',
                    }}
                    legend={false}
                    xField={'name'}
                    yField={'count'}
                    colorField={'name'}
                  />
                ),
              },
            ]}
          />
        </Col>
        <Col span={24}>
          <HTML>{track?.description}</HTML>
        </Col>
      </Row>
    </Page>
  )
}

export { Track as default }
